import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportRatioType,
} from "@common/constants";

export enum AccountingReportType {
    PROFIT_LOSS = "profit-loss",
    BALANCE_SHEET = "balance-sheet",
    TRIAL_BALANCE = "trial-balance",
    GENERAL_LEDGER = "general-ledger",
    EXPENSE_BY_VENDOR = "expense-by-vendor",
    CASH_FLOW_STATEMENT = "cash-flow-statement",
    ELIMINATION_DETAIL = "elimination-detail",
    ELIMINATION_EXCEPTIONS = "elimination-exceptions",
    SCHEDULES = "schedules",
    AR_AGING_SUMMARY = "ar-aging-summary",
    AR_AGING_DETAIL = "ar-aging-detail",
    CHECK_DETAIL = "check-detail",
}

export const ACCOUNTING_REPORT_LABELS: Record<AccountingReportType, string> = {
    [AccountingReportType.PROFIT_LOSS]: "Profit & Loss",
    [AccountingReportType.BALANCE_SHEET]: "Balance Sheet",
    [AccountingReportType.TRIAL_BALANCE]: "Trial Balance",
    [AccountingReportType.GENERAL_LEDGER]: "General Ledger",
    [AccountingReportType.EXPENSE_BY_VENDOR]: "Expenses by Vendor",
    [AccountingReportType.CASH_FLOW_STATEMENT]: "Cash Flow Statement",
    [AccountingReportType.ELIMINATION_DETAIL]: "Elimination Ledger",
    [AccountingReportType.ELIMINATION_EXCEPTIONS]:
        "Intercompany Exceptions Report",
    [AccountingReportType.SCHEDULES]: "Recognition schedules",
    [AccountingReportType.AR_AGING_SUMMARY]: "A/R Aging Summary",
    [AccountingReportType.AR_AGING_DETAIL]: "A/R Aging Detail",
    [AccountingReportType.CHECK_DETAIL]: "Check and Deposit Detail",
};

export interface AccountingReportComparisonOptions {
    comparisons: readonly AccountingReportComparison[];
    comparisonDiffs: readonly AccountingReportComparisonDiff[];
    ratioTypes: readonly AccountingReportRatioType[];
}

export const NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS: AccountingReportComparisonOptions =
    { comparisons: [], comparisonDiffs: [], ratioTypes: [] };

export const ACCOUNTING_REPORT_COMPARISON_OPTIONS = {
    [AccountingReportType.PROFIT_LOSS]: {
        comparisons: [
            AccountingReportComparison.PREVIOUS_YEAR,
            AccountingReportComparison.PREVIOUS_PERIOD,
        ],
        comparisonDiffs: [
            AccountingReportComparisonDiff.PERCENTAGE,
            AccountingReportComparisonDiff.ABSOLUTE,
        ],
        ratioTypes: [
            AccountingReportRatioType.INCOME_PERCENTAGE,
            AccountingReportRatioType.EXPENSE_PERCENTAGE,
        ],
    },
    [AccountingReportType.BALANCE_SHEET]: {
        comparisons: [
            AccountingReportComparison.PREVIOUS_YEAR,
            AccountingReportComparison.PREVIOUS_PERIOD,
        ],
        comparisonDiffs: [
            AccountingReportComparisonDiff.PERCENTAGE,
            AccountingReportComparisonDiff.ABSOLUTE,
        ],
        ratioTypes: [],
    },
    [AccountingReportType.TRIAL_BALANCE]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.GENERAL_LEDGER]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.EXPENSE_BY_VENDOR]: {
        comparisons: [
            AccountingReportComparison.PREVIOUS_YEAR,
            AccountingReportComparison.PREVIOUS_PERIOD,
        ],
        comparisonDiffs: [
            AccountingReportComparisonDiff.PERCENTAGE,
            AccountingReportComparisonDiff.ABSOLUTE,
        ],
        ratioTypes: [],
    },
    [AccountingReportType.CASH_FLOW_STATEMENT]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.ELIMINATION_DETAIL]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.ELIMINATION_EXCEPTIONS]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.SCHEDULES]: NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.AR_AGING_SUMMARY]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.AR_AGING_DETAIL]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    [AccountingReportType.CHECK_DETAIL]:
        NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
} as const satisfies Record<
    AccountingReportType,
    AccountingReportComparisonOptions
>;
